import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Subject, takeUntil, take, distinctUntilChanged, Observable } from 'rxjs';
import { IBrandDetails } from 'src/app/core/domain/brand.model';
import { IProfileDetails } from 'src/app/core/domain/profile.model';

import { IProgram, MembershipPlans } from 'src/app/core/domain/program.model';
import { ModalHandlerService } from 'src/app/core/service/modal-handler.service';
import { PaymentsService } from 'src/app/core/service/payments.service';
import { ProgramOfficeService } from 'src/app/core/service/program-office.service';
import { getBrandDetails } from 'src/app/core/state/global';
import { getProfileData } from 'src/app/core/state/profile';

interface PlanType {
  type: string;
  value: string;
  raw: MembershipPlans;
}

interface PlanTerm {
  type: string;
  label: string;
  price: number;
}

@Component({
  selector: 'app-membership-plans',
  templateUrl: './membership-plans.component.html',
  styleUrls: ['./membership-plans.component.scss'],
})
export class MembershipPlansComponent implements OnInit, OnDestroy {
  @Input() applicantId!: number | null;
  
  @Input() selectedProgram!: IProgram;
  @Input() moduleType!: string;
  @Input() currentRoundId!: number;
  @Output() verifyPaymentResponse = new EventEmitter<any>();

  @Input() accountType:string;
  @Input () currentPlanId :number=null;
  @Input () upgradeWithMembershipTypeId :number=null;
   membershiplan:MembershipPlans[]=[];

  formGroup!: FormGroup;
  planTypes: PlanType[] = [];
  planTerms: PlanTerm[] = [];
  isAmount
  currency:any;
  private destroy$ = new Subject<void>();
  public getProfileData$: Observable<IProfileDetails>;
    profileData: IProfileDetails;
 brandDetails: Partial<IBrandDetails>;
  constructor(
    private fb: FormBuilder,
    private modalHandler: ModalHandlerService,
    private router: Router,
    private paymentsService: PaymentsService,
    private programofficeservice:ProgramOfficeService,
    private readonly store: Store
  ) {}

  ngOnInit(): void {
    this.store.pipe(select(getBrandDetails)).pipe(distinctUntilChanged()).subscribe((res) => {
      if (res) {
        this.brandDetails = res;
         this.currency= this.brandDetails.features.payment_currency ;
      }
    })

    this.getProfileData$ = this.store.pipe(select(getProfileData));
    this.getProfileData$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((res) => {
      if (res) {
        this.profileData = res;
      }
    })
    this.formGroup = this.fb.group({
      planType: [null, Validators.required],
      planTerm: [null, Validators.required],
    });

    if (this.accountType) {
      localStorage.removeItem(`lastSelectedPlanType`);
      localStorage.removeItem(`lastSelectedPlanTerm`);
      this.initializeMembershipPlans();
    }else{
    this.verifyPayment();
    this.initializePlans();}
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** ----------- INIT METHODS ----------- */

  private verifyPayment(): void {
    this.paymentsService
      .getEventVerifyPayment(this.moduleType, this.selectedProgram?.id, this.applicantId, this.currentRoundId)
      .pipe(take(1))
      .subscribe({
        next: (res) => this.verifyPaymentResponse.emit(res),
        error: (err) => {
          this.verifyPaymentResponse.emit(null);
        },
      });
  }

  private initializePlans(): void {
    this.programofficeservice
      .getMembershipData(this.moduleType,this.selectedProgram.id, this.currentRoundId)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
        
          this.selectedProgram.membershipTypes = res.membershipsTypes || [];
          if (!this.selectedProgram.membershipTypes.length) return;

          this.planTypes = this.selectedProgram.membershipTypes.map((plan) => ({
            type: plan.code,
            value: plan.name,
            raw: plan,
          }));

          // Default terms from the first plan
          if (this.planTypes.length) {
            this.setPlanTerms(this.planTypes[0].raw);
          }

          this.initializeForm();
        },
        error: (err) => {
          console.error('Failed to fetch membership plans:', err);
        },
      });
  }

 private initializeMembershipPlans() {
  this.paymentsService
    .getMembershipPalns(this.accountType)
    .pipe(take(1))
    .subscribe({
      next: (res: any) => {
        let plans = (res?.membershipsTypes || []) as MembershipPlans[];
        if (!plans.length) return;

         if(this.upgradeWithMembershipTypeId){
            plans = plans.filter(plan => plan.id == this.upgradeWithMembershipTypeId);
        }
        // if (this.currentPlanId && !this.upgradeWithMembershipTypeId) {
        //   plans = plans.filter(plan => plan.id >= this.currentPlanId);
        // }
       
        this.membershiplan = plans;

        this.planTypes = this.membershiplan.map((plan) => ({
          type: plan.code,
          value: plan.name,
          raw: plan,
        }));

        if (this.planTypes.length) {
          this.setPlanTerms(this.planTypes[0].raw);
        }

        this.initializeForm();
      },
      error: (err) => {
        console.error('Failed to fetch membership plans:', err);
      },
    });
}

private initializeForm(): void {
  const lastSelectedPlanType = localStorage.getItem('lastSelectedPlanType');
  const lastSelectedPlanTerm = localStorage.getItem('lastSelectedPlanTerm');

  this.formGroup.patchValue({
    planType: lastSelectedPlanType || this.planTypes[0]?.type || null,
    planTerm: lastSelectedPlanTerm || this.planTerms[0]?.type || null,
  });

  this.formGroup.get('planType')?.valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe((code) => {
      if(code && !this.accountType){
      localStorage.setItem('lastSelectedPlanType', code);
      }
      const selectedPlan = this.planTypes.find((p) => p.type === code)?.raw;
      if (selectedPlan) {
        this.setPlanTerms(selectedPlan);

        // restore last term if still valid, else pick first available
        const savedTerm = localStorage.getItem('lastSelectedPlanTerm');
        const validTerm = this.planTerms.find(t => t.type === savedTerm);

        this.formGroup.patchValue({
          planTerm: validTerm?.type || this.planTerms[0]?.type || null
        });
      }
    });

  this.formGroup.get('planTerm')?.valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe((term) => {
      if (term && !this.accountType) {
        localStorage.setItem('lastSelectedPlanTerm', term);
      }
    });
}


  get selectedPlanPrice(): number | null {
  const selectedType = this.formGroup.get('planTerm')?.value;
  const selectedOption = this.planTerms.find(option => option.type === selectedType);
  return selectedOption?.price ?? null;
}

  private setPlanTerms(plan: MembershipPlans): void {
    this.planTerms = [
      { type: 'monthly', label: 'Monthly', price: +plan.monthlyPricing || 0 },
      { type: 'quarterly', label: 'Quarterly', price: +plan.quarterlyPricing || 0 },
      { type: 'halfYearly', label: 'Half Yearly', price: +plan.halfyearlyPricing || 0 },
      { type: 'yearly', label: 'Yearly', price: +plan.yearlyPricing || 0 },
      { type: 'lifetime', label: 'Life Time', price: +plan.lifetimePricing || 0 },
    ].filter((term) => term.price > 0);
  }

  onSubmit(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const { planType, planTerm } = this.formGroup.value;
    const selectedPlan = this.planTypes.find((p) => p.type === planType)?.raw;
    if (!selectedPlan) return;

    const selectedPrice =
      this.planTerms.find((t) => t.type === planTerm)?.price ?? 0;

    this.openCheckoutModal(planTerm, selectedPrice, selectedPlan);
  }
 private openCheckoutModal(selectedCycle: string, selectedPrice: number, plan: MembershipPlans): void {
  const openModal = (activeProgramUrl: string) => {
    const isMembershipRenewOrUpgrade = Boolean(
      this.accountType && this.currentPlanId && this.modalHandler?.PAYMENTDETAILS?.endDate
    );
    const currentMembershipEndDate = isMembershipRenewOrUpgrade
      ? this.modalHandler?.PAYMENTDETAILS?.endDate ?? null
      : null;

    this.modalHandler
      .openCheckoutModal({
        moduleType: this.moduleType ||'membership',
        selectedCycle,
        selectedPrice,
        userType: 'registered',
        additionalData: {
          planType: plan.code,
          moduleTypeId: this.selectedProgram?.id ||  plan.id,
          moduleTypeSubId: this.currentRoundId,
          name: plan.name,
          chargesTypes: plan.chargesTypes,
          charges: plan.charges,
          canBookMultiple: plan.canBookMultiple,
          taxProfile: plan.taxProfile,
          maxDurationForMultipleBooking: plan.maxDurationForMultipleBooking,
          isChargesIncluded: plan.isChargesIncluded,
          isTaxIncluded: plan.isTaxIncluded,
          isCouponsIncluded: plan.isCouponsIncluded,
          membershipid: plan.id,
          applicantId: this.applicantId,
          activeProgramUrl,
           maxYears: plan.maxYears,
           expiryMonth: plan.expiryMonth,
           isHardExpiry: plan.isHardExpiry,
          currentMembershipEndDate,
          isMembershipRenewOrUpgrade,
           // currency:this.currency,
           paymentType: 'membership',
           profileName:this.profileData?.profileName
        },
      })
      .then((result) => console.log('Modal closed with:', result))
      .catch((reason) => console.log('Modal dismissed:', reason));
  };

  if (this.upgradeWithMembershipTypeId) {

    this.router.navigate(['/account/edit/membership']).then(() => {
      const activeProgramUrl = this.router.url.replace(/^\//, '');
      console.log('Active Program URL after navigation:', activeProgramUrl);
      openModal(activeProgramUrl);
    });
  } else {
    const activeProgramUrl = this.router.url.replace(/^\//, '');
    console.log('Active Program URL (no navigation):', activeProgramUrl);
    openModal(activeProgramUrl);
  }
}

}
